import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { axiosDelete, axiosGet, axiosPatch, axiosPost } from 'Redux/axiosHelper'
import { ItemsToBringListing } from 'types/listing/listings'

// NOTE: Backend amenity shape matches items-to-bring (name + icon), so we
// reuse ItemsToBringListing for now. If a dedicated Amenity type is added
// later, swap it in here.
export type AmenityListing = ItemsToBringListing

// get amenities
const getAmenities = async (params?: { organizationId: string }) => {
  const response = await axiosGet(`v1/amenity`, params)
  return response.data
}

export const useGetAmenities = (params?: { organizationId: string }) => {
  const amenities = useQuery<AmenityListing[]>(
    ['amenities-list'],
    () => getAmenities(params),
    {
      enabled: false,
    },
  )

  return amenities
}

// create amenity
const createAmenity = async (payload: {
  name: string
  iconId: string
  organizationId?: string
}) => {
  const response = await axiosPost(payload, `v1/amenity`)
  return response.data
}

export const useCreateAmenity = () => {
  const queryClient = useQueryClient()

  return useMutation(createAmenity, {
    onSuccess: (newAmenity: AmenityListing) => {
      queryClient.setQueryData(
        ['amenities-list'],
        (oldData: AmenityListing[] | undefined) => {
          if (!oldData) return [newAmenity]
          return [...oldData, newAmenity]
        },
      )
    },
  })
}

// update amenity
const updateAmenity = async ({
  amenityId,
  payload,
}: {
  amenityId: string
  payload: {
    name: string
    iconId: string
    organizationId?: string
  }
}) => {
  const response = await axiosPatch(payload, `v1/amenity/${amenityId}`)
  return response.data
}

export const useUpdateAmenity = () => {
  const queryClient = useQueryClient()

  return useMutation(updateAmenity, {
    onSuccess: (updatedAmenity: AmenityListing) => {
      queryClient.setQueryData(
        ['amenities-list'],
        (oldData: AmenityListing[] | undefined) => {
          if (!oldData) return [updatedAmenity]
          return oldData.map((item: AmenityListing) =>
            item._id === updatedAmenity._id ? updatedAmenity : item,
          )
        },
      )
    },
  })
}

// delete amenity
const deleteAmenity = async (amenityId: string) => {
  const response = await axiosDelete(`v1/amenity/${amenityId}`)
  return response.data
}

export const useDeleteAmenity = () => {
  const queryClient = useQueryClient()

  return useMutation(deleteAmenity, {
    onSuccess: (deletedAmenity: AmenityListing) => {
      queryClient.setQueryData(
        ['amenities-list'],
        (oldData: AmenityListing[] | undefined) => {
          if (!oldData) return []
          return oldData.filter(
            (item: AmenityListing) => item._id !== deletedAmenity._id,
          )
        },
      )
    },
  })
}
